/*
 * Jolly Window: motion and the buy flow. No libraries.
 * - reveals: one IntersectionObserver adds .is-in (CSS does the blur-rise, the icing roofline, the panes peeling
 *   on, the sill's candle glow coming up, the final peel-on)
 * - nav: turns into a fir bar once the page scrolls
 * - snow: line-art snowflakes (six-armed crystals like the clings' own, plus soft dots) drift down a canvas in
 *   each [data-snow] section, turning slowly; it runs only while the section is on screen, never under reduced
 *   motion
 * - gallery: thumbs switch the photo (works with or without a product); the bundle-2 photo shows only while 2 sets
 *   are picked
 * - how: a rAF loop (only while the section is on screen) reads scroll progress; each step's photo peels on over
 *   the last (--t on the photo), the copy swaps, the 2 x 2 pane map lights up
 * - reveal: the smoothing card follows the scroll down the glass until the visitor drags it or uses the range
 * - FAQ and the buy box's accordions, delivery dates, the Christmas order-by line hiding itself after its date
 * - commerce (theme-v3's plumbing): one selection (sets + a design per set) shared by the buy box, the compact
 *   buy modules (1 / 2 / 3 chips) and the sticky bar; mixed designs go in as separate lines; the cart drawer
 *   (steppers, bundle saving from the cart's real discounts, second-set nudge, opt-in shipping protection); CTAs
 *   scroll to the nearest buy module
 * Reduced motion: no snow, the how-to is a static list, the card rests halfway.
 */
(function () {
  'use strict';

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var root = document.documentElement;
  var $ = function (sel, el) {
    return (el || document).querySelector(sel);
  };
  var $$ = function (sel, el) {
    return Array.prototype.slice.call((el || document).querySelectorAll(sel));
  };
  var clamp = function (v, a, b) {
    return Math.min(b, Math.max(a, v));
  };

  /* ───────── reveals ───────── */
  function reveals() {
    var els = $$('[data-reveal], [data-reveal-class], [data-peel]');
    if (!('IntersectionObserver' in window) || reduced) {
      els.forEach(function (el) {
        el.classList.add('is-in');
      });
      return;
    }
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) {
            e.target.classList.add('is-in');
            io.unobserve(e.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
    );
    els.forEach(function (el) {
      io.observe(el);
    });
  }

  /* ───────── nav ───────── */
  function nav() {
    var s = document.createElement('div');
    s.setAttribute('aria-hidden', 'true');
    s.style.cssText = 'position:absolute;top:0;left:0;width:1px;height:120px;pointer-events:none;';
    document.body.prepend(s);
    if (!('IntersectionObserver' in window)) return;
    new IntersectionObserver(function (e) {
      root.classList.toggle('is-scrolled', !e[0].isIntersecting);
    }).observe(s);
  }

  /* ───────── snow: line-art crystals and soft dots, per [data-snow] section ───────── */
  function snow() {
    if (reduced || !window.HTMLCanvasElement) return;
    var hosts = $$('[data-snow]');
    if (!hosts.length) return;
    var dpr = Math.min(2, window.devicePixelRatio || 1);
    function crystal(r, lw, a) {
      var m = r + lw + 2;
      var c = document.createElement('canvas');
      c.width = c.height = Math.ceil(m * 2 * dpr);
      var x = c.getContext('2d');
      x.scale(dpr, dpr);
      x.translate(m, m);
      x.strokeStyle = 'rgba(255, 250, 240, ' + a + ')';
      x.lineWidth = lw;
      x.lineCap = 'round';
      for (var k = 0; k < 6; k++) {
        x.beginPath();
        x.moveTo(0, 0);
        x.lineTo(0, -r);
        x.moveTo(0, -r * 0.56);
        x.lineTo(-r * 0.27, -r * 0.8);
        x.moveTo(0, -r * 0.56);
        x.lineTo(r * 0.27, -r * 0.8);
        x.stroke();
        x.rotate(Math.PI / 3);
      }
      return { c: c, m: m };
    }
    var sprites = [crystal(9, 1.5, 0.92), crystal(7, 1.35, 0.88), crystal(5.2, 1.15, 0.8), crystal(3.8, 1, 0.7)];
    hosts.forEach(function (host) {
      var cv = document.createElement('canvas');
      cv.className = 'snow';
      cv.setAttribute('aria-hidden', 'true');
      host.insertBefore(cv, host.firstChild);
      var ctx = cv.getContext('2d');
      var W = 0;
      var H = 0;
      var flakes = [];
      var on = false;
      var raf = 0;
      var last = 0;
      function make(anywhere) {
        var dot = Math.random() < 0.55;
        var sp = sprites[Math.floor(Math.random() * sprites.length)];
        var depth = dot ? 0.4 + Math.random() * 0.4 : sp.m / 12;
        return {
          x: Math.random() * W,
          y: anywhere ? Math.random() * H : -24,
          dot: dot,
          r: 0.7 + Math.random() * 1.7,
          sp: sp,
          vy: 9 + depth * 26 + Math.random() * 8,
          sw: 6 + Math.random() * 18,
          ph: Math.random() * 6.283,
          f: 0.25 + Math.random() * 0.5,
          rot: Math.random() * 6.283,
          vr: (Math.random() - 0.5) * 0.7,
          a: dot ? 0.3 + Math.random() * 0.45 : 0.55 + Math.random() * 0.4
        };
      }
      function size() {
        var r = host.getBoundingClientRect();
        if (Math.abs(r.width - W) < 1 && Math.abs(r.height - H) < 1) return;
        W = r.width;
        H = r.height;
        cv.width = Math.round(W * dpr);
        cv.height = Math.round(H * dpr);
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        var n = Math.round(Math.min(80, Math.max(16, (W * H) / 24000)));
        flakes = [];
        for (var i = 0; i < n; i++) flakes.push(make(true));
      }
      function frame(t) {
        var dt = last ? Math.min(0.05, (t - last) / 1000) : 0;
        last = t;
        ctx.clearRect(0, 0, W, H);
        for (var i = 0; i < flakes.length; i++) {
          var f = flakes[i];
          f.y += f.vy * dt;
          f.ph += f.f * dt;
          f.rot += f.vr * dt;
          if (f.y > H + 24) {
            flakes[i] = f = make(false);
          }
          var x = f.x + Math.sin(f.ph) * f.sw;
          ctx.globalAlpha = f.a;
          if (f.dot) {
            ctx.beginPath();
            ctx.arc(x, f.y, f.r, 0, 6.283);
            ctx.fillStyle = '#fffaf0';
            ctx.fill();
          } else {
            ctx.save();
            ctx.translate(x, f.y);
            ctx.rotate(f.rot);
            ctx.drawImage(f.sp.c, -f.sp.m, -f.sp.m, f.sp.m * 2, f.sp.m * 2);
            ctx.restore();
          }
        }
        ctx.globalAlpha = 1;
        raf = on ? requestAnimationFrame(frame) : 0;
      }
      size();
      if ('ResizeObserver' in window) {
        var rt = 0;
        new ResizeObserver(function () {
          clearTimeout(rt);
          rt = setTimeout(size, 150);
        }).observe(host);
      }
      if (!('IntersectionObserver' in window)) return;
      new IntersectionObserver(function (e) {
        on = e[0].isIntersecting;
        if (on && !raf) {
          last = 0;
          raf = requestAnimationFrame(frame);
        }
      }).observe(host);
    });
  }

  /* ───────── gallery ───────── */
  function gallery() {
    var gal = $('[data-gal]');
    if (!gal) return;
    var imgs = $$('.gal__main img', gal);
    var thumbs = $$('[data-gal-thumb]', gal);
    var label = $('[data-gal-label]', gal);
    var main = $('.gal__main', gal);
    function show(k) {
      main.classList.add('is-ready');
      imgs.forEach(function (im, j) {
        if (j === k) im.loading = 'eager';
        im.classList.toggle('is-on', j === k);
      });
      thumbs.forEach(function (t, j) {
        t.setAttribute('aria-current', j === k ? 'true' : 'false');
      });
      if (label && thumbs[k] && thumbs[k].getAttribute('data-shot')) {
        label.textContent = label.textContent.replace(/[^\s]+$/, thumbs[k].getAttribute('data-shot'));
      }
    }
    if (imgs.length) show(0);
    thumbs.forEach(function (t, k) {
      t.addEventListener('click', function () {
        show(k);
      });
    });
    gal.show = show;
    /* photos tied to a tier (data-tier) show only while that many sets are picked; picking the tier shows its photo */
    gal.tier = function (q, jump) {
      var cur = -1;
      imgs.forEach(function (im, j) {
        if (im.classList.contains('is-on')) cur = j;
      });
      thumbs.forEach(function (t, j) {
        var tier = t.getAttribute('data-tier');
        if (!tier) return;
        var on = Number(tier) === q;
        t.hidden = !on;
        if (on && jump) show(j);
        else if (!on && cur === j) show(0);
      });
    };
  }

  /* ───────── how it goes on ───────── */
  function how() {
    var el = $('[data-how]');
    if (!el) return;
    var photos = $$('[data-ph]', el);
    var beats = $$('[data-beat]', el);
    var cells = $$('[data-cell]', el);
    var N = beats.length;
    if (!N) return;
    if (reduced) {
      el.classList.add('is-static');
      return;
    }
    var beat = -1;
    var cur = -1;
    var on = false;
    var raf = 0;
    var outT = [];

    function setBeat(b) {
      if (b === beat) return;
      beat = b;
      beats.forEach(function (node, k) {
        if (k === b) {
          node.classList.remove('is-out');
          node.classList.add('is-on');
        } else if (node.classList.contains('is-on')) {
          node.classList.remove('is-on');
          node.classList.add('is-out');
          clearTimeout(outT[k]);
          outT[k] = setTimeout(function () {
            node.classList.remove('is-out');
          }, 750);
        }
      });
      cells.forEach(function (c, k) {
        c.classList.toggle('is-done', k < b);
        c.classList.toggle('is-now', k === b);
      });
    }
    function render(p) {
      // the scroll is split into N beats; photo k (k >= 1) peels on over the hand-over into beat k
      var x = p * N;
      photos.forEach(function (ph, k) {
        if (k === 0) return;
        var t = clamp((x - k + 0.42) / 0.62, 0, 1);
        t = t * t * (3 - 2 * t);
        ph.style.setProperty('--t', t.toFixed(4));
      });
      setBeat(Math.min(N - 1, Math.floor(x * 0.9999)));
    }
    function progress() {
      var r = el.getBoundingClientRect();
      var span = r.height - window.innerHeight;
      return span > 0 ? clamp(-r.top / span, 0, 1) : 0;
    }
    function loop() {
      var target = progress();
      if (cur < 0) cur = target;
      cur += (target - cur) * 0.14;
      if (Math.abs(target - cur) < 0.0004) cur = target;
      render(cur);
      raf = on ? requestAnimationFrame(loop) : 0;
    }
    render(0);
    new IntersectionObserver(function (e) {
      on = e[0].isIntersecting;
      if (on && !raf) raf = requestAnimationFrame(loop);
    }).observe(el);
  }

  /* ───────── the reveal: the smoothing card ───────── */
  function reveal() {
    var ba = $('[data-ba]');
    if (!ba) return;
    var range = $('[data-ba-range]');
    var touched = false;
    function set(v) {
      v = clamp(v, 0, 100);
      ba.style.setProperty('--cut', v.toFixed(2) + '%');
      if (range) range.value = Math.round(v);
    }
    if (range) {
      range.addEventListener('input', function () {
        touched = true;
        set(Number(range.value));
      });
    }
    var dragging = false;
    function fromPointer(e) {
      var r = ba.getBoundingClientRect();
      set(((e.clientY - r.top) / r.height) * 100);
    }
    ba.addEventListener('pointerdown', function (e) {
      touched = true;
      dragging = true;
      ba.setPointerCapture(e.pointerId);
      fromPointer(e);
    });
    ba.addEventListener('pointermove', function (e) {
      if (dragging) fromPointer(e);
    });
    ['pointerup', 'pointercancel'].forEach(function (n) {
      ba.addEventListener(n, function () {
        dragging = false;
      });
    });
    if (reduced) return set(50);
    // follow the scroll: the card starts at the top as the window enters and reaches the sill as it leaves centre
    var on = false;
    var raf = 0;
    var cur = -1;
    function loop() {
      if (!touched) {
        var r = ba.getBoundingClientRect();
        var vh = window.innerHeight;
        var p = clamp((vh * 0.92 - r.top) / (vh * 0.92 - (vh * 0.5 - r.height * 0.5)) , 0, 1);
        var target = 4 + p * 92;
        if (cur < 0) cur = target;
        cur += (target - cur) * 0.12;
        set(cur);
      }
      raf = on && !touched ? requestAnimationFrame(loop) : 0;
    }
    set(4);
    new IntersectionObserver(function (e) {
      on = e[0].isIntersecting;
      if (on && !raf) raf = requestAnimationFrame(loop);
    }).observe(ba);
  }

  /* ───────── FAQ ───────── */
  function faq() {
    $$('.qa__q').forEach(function (q) {
      q.addEventListener('click', function () {
        var open = q.getAttribute('aria-expanded') !== 'true';
        q.setAttribute('aria-expanded', open ? 'true' : 'false');
        var card = q.closest('.qa');
        if (card) card.classList.toggle('is-open', open);
      });
    });
  }

  /* ───────── delivery dates (calendar days from today, or business days with a "b") ───────── */
  function dates() {
    var fmt;
    try {
      fmt = new Intl.DateTimeFormat(document.documentElement.lang || 'en', { month: 'short', day: 'numeric' });
    } catch (e) {
      return;
    }
    var day = function (n) {
      var d = new Date();
      d.setDate(d.getDate() + n);
      return d;
    };
    var businessDay = function (n) {
      var d = new Date();
      while (n > 0) {
        d.setDate(d.getDate() + 1);
        if (d.getDay() !== 0 && d.getDay() !== 6) n--;
      }
      return d;
    };
    $$('[data-date]').forEach(function (el) {
      var raw = el.getAttribute('data-date');
      if (/b$/.test(raw)) {
        el.textContent = fmt.format(businessDay(parseInt(raw, 10)));
        return;
      }
      var parts = raw.split('-').map(Number);
      if (parts.length === 1) {
        el.textContent = fmt.format(day(parts[0]));
        return;
      }
      var a = day(parts[0]);
      var b = day(parts[1]);
      el.textContent = a.getMonth() === b.getMonth() ? fmt.format(a) + '–' + b.getDate() : fmt.format(a) + ' – ' + fmt.format(b);
    });
    // the Christmas order-by line shows until the end of its day (a cached page never shows it late)
    $$('[data-until]').forEach(function (el) {
      var end = new Date(el.getAttribute('data-until') + 'T23:59:59');
      if (!isNaN(end) && new Date() > end) el.hidden = true;
    });
  }

  /* ───────── commerce: one selection shared by every buy module (theme-v3's plumbing) ───────── */
  function money(cents) {
    var cur = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: cur }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  }
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* CTAs scroll to the nearest buy module: the buy box (its panel, at the top) or a compact module (centred) */
  function toNearestModule() {
    var mods = $$('[data-buy-module]');
    if (!mods.length) return;
    var best = mods[0];
    var bestD = Infinity;
    mods.forEach(function (m) {
      var r = m.getBoundingClientRect();
      var d = Math.abs(r.top + r.height / 2 - window.innerHeight / 2);
      if (d < bestD) {
        bestD = d;
        best = m;
      }
    });
    var isMain = best.getAttribute('data-buy-module') === 'main';
    var target = isMain ? $('.buy__panel', best) || best : $('.mini__card', best) || best;
    target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: isMain ? 'start' : 'center' });
  }

  function commerce() {
    // CTAs work even without a product (the preview buy box)
    document.addEventListener('click', function (e) {
      var cta = e.target.closest('[data-cta]');
      if (cta) {
        e.preventDefault();
        toNearestModule();
      }
    });

    var dataEl = $('#wv-data');
    if (!dataEl) return;
    var D;
    try {
      D = JSON.parse(dataEl.textContent);
    } catch (e) {
      return;
    }
    var T = D.text || {};
    var V = D.variants || [];
    if (!V.length) return;
    var byTitle = {};
    var byId = {};
    V.forEach(function (v) {
      byTitle[v.title] = v;
      byId[v.id] = v;
    });
    var names = V.map(function (v) {
      return v.title;
    });
    var unit = V[0].price;
    var compareAt = V[0].compare;
    var base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var main = $('[data-buy-module="main"]');
    var mainForm = main ? $('form[action*="/cart/add"]', main) : null;

    function offFor(q) {
      var off = 0;
      (D.tiers || []).forEach(function (t) {
        if (q >= t.qty) off = t.off;
      });
      return off;
    }

    var startQty = D.defaultQty || 1;
    var checkedTier = main ? $('input[name="quantity"][data-qty]:checked', main) : null;
    if (checkedTier) startQty = Number(checkedTier.getAttribute('data-qty'));
    var S = { qty: startQty, picks: [names[0], names[0], names[0]] };
    var subs = [];
    function emit() {
      subs.forEach(function (f) {
        f();
      });
    }
    function chosen() {
      return S.picks.slice(0, S.qty);
    }
    function isMixed() {
      var c = chosen();
      return c.some(function (x) {
        return x !== c[0];
      });
    }
    function totals() {
      var line = unit * S.qty;
      // the same rounding as Shopify's automatic discounts: the per-unit discount is rounded down
      var total = S.qty * (unit - Math.floor((unit * offFor(S.qty)) / 100));
      var was = S.qty > 1 ? line : compareAt;
      return { total: total, was: was };
    }
    function items() {
      var m = {};
      chosen().forEach(function (c) {
        m[c] = (m[c] || 0) + 1;
      });
      return Object.keys(m).map(function (k) {
        return { id: byTitle[k].id, quantity: m[k] };
      });
    }

    var gal = $('[data-gal]');
    var jump = false; // true when the visitor picks a tier, so its photo shows (never on page load)
    function setQty(q) {
      for (var i = S.qty; i < q; i++) S.picks[i] = S.picks[S.qty - 1];
      jump = q !== S.qty;
      S.qty = q;
      emit();
    }
    function paint() {
      var t = totals();
      $$('[data-sel-qty]').forEach(function (el) {
        el.textContent = S.qty + ' ' + (S.qty > 1 ? T.many : T.one);
      });
      $$('[data-sel-total]').forEach(function (el) {
        el.textContent = money(t.total);
      });
      $$('[data-sel-was]').forEach(function (el) {
        el.textContent = money(t.was);
        el.hidden = !(t.was > t.total);
      });
      $$('[data-sel-img]').forEach(function (el) {
        var src = byTitle[chosen()[0]].img;
        if (src && el.getAttribute('src') !== src) el.setAttribute('src', src);
      });
      $$('[data-qty-pick]').forEach(function (b) {
        b.setAttribute('aria-pressed', Number(b.getAttribute('data-qty-pick')) === S.qty ? 'true' : 'false');
      });
      if (gal && gal.tier) gal.tier(S.qty, jump);
      jump = false;
      if (main) {
        $$('input[name="quantity"][data-qty]', main).forEach(function (r) {
          r.checked = Number(r.getAttribute('data-qty')) === S.qty;
        });
        $$('[data-unit-row]', main).forEach(function (row, i) {
          row.hidden = i >= S.qty;
          $$('input[data-colour]', row).forEach(function (r) {
            r.checked = r.value === S.picks[i];
          });
        });
        var mix = $('[data-mix-label]', main);
        if (mix) mix.textContent = isMixed() ? T.mixed : chosen()[0];
        var idIn = $('[data-variant-id]', main);
        if (idIn) idIn.value = byTitle[chosen()[0]].id;
        var express = $('[data-express]', main);
        var note = $('[data-mixed-note]', main);
        if (express) express.classList.toggle('is-hidden', isMixed());
        if (note) note.hidden = !isMixed();
      }
    }
    subs.push(paint);

    if (main) {
      $$('input[name="quantity"][data-qty]', main).forEach(function (r) {
        r.addEventListener('change', function () {
          if (r.checked) setQty(Number(r.getAttribute('data-qty')));
        });
      });
      $$('[data-unit-row]', main).forEach(function (row, i) {
        $$('input[data-colour]', row).forEach(function (r) {
          r.addEventListener('change', function () {
            if (!r.checked) return;
            S.picks[i] = r.value;
            emit();
          });
        });
      });
    }
    document.addEventListener('click', function (e) {
      var qp = e.target.closest('[data-qty-pick]');
      if (qp) {
        setQty(Number(qp.getAttribute('data-qty-pick')));
        return;
      }
      var add = e.target.closest('[data-add-sel]');
      if (add) addSelection(add);
    });

    function post(url, body) {
      return fetch(base + url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(body)
      }).then(function (r) {
        return r.json().then(function (j) {
          if (!r.ok) throw j;
          return j;
        });
      });
    }
    function addSelection(btn) {
      if (btn) {
        btn.disabled = true;
        btn.setAttribute('aria-busy', 'true');
      }
      var mod = btn && btn.closest('[data-buy-module]');
      var msg = mod ? $('[data-msg]', mod) : null;
      if (msg) {
        msg.textContent = '';
        msg.classList.remove('is-err');
      }
      return post('cart/add.js', { items: items() })
        .then(function () {
          return drawer.open();
        })
        .catch(function (err) {
          var text = (err && (err.description || err.message)) || T.error;
          if (msg) {
            msg.textContent = text;
            msg.classList.add('is-err');
          } else {
            window.alert(text);
          }
        })
        .then(function () {
          if (btn) {
            btn.disabled = false;
            btn.removeAttribute('aria-busy');
          }
        });
    }
    if (mainForm) {
      mainForm.addEventListener('submit', function (e) {
        if (!window.fetch) return;
        e.preventDefault();
        addSelection($('[data-add]', mainForm));
      });
    }

    /* ── the cart drawer ── */
    var drawer = (function () {
      var el = $('[data-drawer]');
      if (!el) {
        return {
          open: function () {
            window.location.href = base + 'cart';
          },
          isOpen: function () {
            return false;
          }
        };
      }
      var panel = $('.drawer__panel', el);
      var linesEl = $('[data-lines]', el);
      var empty = $('[data-empty]', el);
      var foot = $('[data-foot]', el);
      var nudge = $('[data-nudge]', el);
      var prot = $('[data-protect]', el);
      var protWrap = $('[data-protect-wrap]', el);
      var P = D.protection;
      var cart = null;
      var lastFocus = null;
      var isOpen = false;
      if (P && $('[data-protect-price]', el)) $('[data-protect-price]', el).textContent = money(P.price);

      function isUnit(it) {
        return !!byId[it.variant_id];
      }
      function protItem() {
        return P && cart
          ? cart.items.filter(function (it) {
              return it.variant_id === P.id;
            })[0]
          : null;
      }
      function unitCount() {
        return cart
          ? cart.items.filter(isUnit).reduce(function (n, it) {
              return n + it.quantity;
            }, 0)
          : 0;
      }
      function refresh() {
        return fetch(base + 'cart.js', { headers: { Accept: 'application/json' } })
          .then(function (r) {
            return r.json();
          })
          .then(function (c) {
            cart = c;
            return tidy();
          })
          .then(render);
      }
      /* protection never outlives the sets, and is never more than one */
      function tidy() {
        var p = protItem();
        if (!p) return Promise.resolve();
        var n = unitCount();
        if (!n || p.quantity > 1) {
          return post('cart/change.js', { id: p.key, quantity: n ? 1 : 0 }).then(function (c) {
            cart = c;
          });
        }
        return Promise.resolve();
      }
      function render() {
        if (!cart) return;
        var n = unitCount();
        var p = protItem();
        var units = cart.items.filter(isUnit);
        linesEl.innerHTML = units
          .map(function (it) {
            var v = byId[it.variant_id];
            var img = (v && v.img) || it.image || '';
            var struck = it.original_line_price > it.final_line_price ? '<s>' + money(it.original_line_price) + '</s>' : '';
            return (
              '<li class="line-item">' +
              '<img src="' + esc(img) + '" alt="" width="72" height="72" loading="lazy">' +
              '<p class="line-item__t"><b>' + esc(it.product_title) + '</b><span>' + esc(it.variant_title || '') + '</span></p>' +
              '<div class="line-item__p"><b>' + money(it.final_line_price) + '</b>' + struck +
              '<span class="step"><button type="button" data-step="-1" data-key="' + esc(it.key) + '" data-q="' + it.quantity + '" aria-label="' + esc(T.decrease) + '">−</button>' +
              '<span>' + it.quantity + '</span>' +
              '<button type="button" data-step="1" data-key="' + esc(it.key) + '" data-q="' + it.quantity + '" aria-label="' + esc(T.increase) + '">+</button></span></div>' +
              '</li>'
            );
          })
          .join('');
        empty.hidden = n > 0;
        foot.hidden = n === 0;
        nudge.hidden = n !== 1;
        if (protWrap) protWrap.hidden = !P || n === 0;
        if (prot) prot.checked = !!p;
        var original = units.reduce(function (s, it) {
          return s + it.original_line_price;
        }, 0);
        var saving = units.reduce(function (s, it) {
          return s + (it.original_line_price - it.final_line_price);
        }, 0);
        $('[data-t-original]', el).textContent = money(original);
        $('[data-t-save-row]', el).hidden = saving <= 0;
        $('[data-t-save]', el).textContent = '−' + money(saving);
        var protRow = $('[data-t-prot-row]', el);
        if (protRow) {
          protRow.hidden = !p;
          if (p) $('[data-t-prot]', el).textContent = money(p.final_line_price);
        }
        $('[data-t-total]', el).textContent = money(cart.total_price);
        var count = $('[data-cart-count]');
        if (count) {
          count.textContent = n;
          count.hidden = !n;
        }
      }
      function change(key, q) {
        el.setAttribute('aria-busy', 'true');
        return post('cart/change.js', { id: key, quantity: q })
          .then(function (c) {
            cart = c;
            return tidy();
          })
          .then(render)
          .catch(function () {
            return refresh();
          })
          .then(function () {
            el.removeAttribute('aria-busy');
          });
      }
      linesEl.addEventListener('click', function (e) {
        var b = e.target.closest('[data-step]');
        if (!b) return;
        var q = Number(b.getAttribute('data-q')) + Number(b.getAttribute('data-step'));
        change(b.getAttribute('data-key'), Math.max(0, q));
      });
      if (nudge) {
        $('[data-nudge-add]', nudge).addEventListener('click', function () {
          var one = cart && cart.items.filter(isUnit)[0];
          if (!one) return;
          el.setAttribute('aria-busy', 'true');
          post('cart/add.js', { items: [{ id: one.variant_id, quantity: 1 }] })
            .then(refresh)
            .then(function () {
              el.removeAttribute('aria-busy');
            });
        });
      }
      if (prot && P) {
        prot.addEventListener('change', function () {
          var p = protItem();
          el.setAttribute('aria-busy', 'true');
          var req = prot.checked
            ? p
              ? Promise.resolve()
              : post('cart/add.js', { items: [{ id: P.id, quantity: 1 }] })
            : p
              ? post('cart/change.js', { id: p.key, quantity: 0 })
              : Promise.resolve();
          req.then(refresh).then(function () {
            el.removeAttribute('aria-busy');
          });
        });
      }
      function open() {
        lastFocus = document.activeElement;
        el.hidden = false;
        isOpen = true;
        root.classList.add('is-drawer-open');
        updateBar();
        requestAnimationFrame(function () {
          el.classList.add('is-open');
          panel.focus({ preventScroll: true });
        });
        return refresh();
      }
      function close() {
        if (!isOpen) return;
        isOpen = false;
        el.classList.remove('is-open');
        root.classList.remove('is-drawer-open');
        updateBar();
        setTimeout(function () {
          if (!isOpen) el.hidden = true;
        }, 650);
        if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
      }
      $$('[data-drawer-close]', el).forEach(function (b) {
        b.addEventListener('click', close);
      });
      document.addEventListener('keydown', function (e) {
        if (!isOpen) return;
        if (e.key === 'Escape') close();
        if (e.key === 'Tab') {
          var f = $$('button, [href], input, [tabindex]:not([tabindex="-1"])', panel).filter(function (x) {
            return x.offsetParent !== null && !x.disabled;
          });
          if (!f.length) return;
          if (e.shiftKey && document.activeElement === f[0]) {
            e.preventDefault();
            f[f.length - 1].focus();
          } else if (!e.shiftKey && document.activeElement === f[f.length - 1]) {
            e.preventDefault();
            f[0].focus();
          }
        }
      });
      $$('.nav__cart').forEach(function (a) {
        a.addEventListener('click', function (e) {
          e.preventDefault();
          open();
        });
      });
      return {
        open: open,
        close: close,
        refresh: refresh,
        isOpen: function () {
          return isOpen;
        }
      };
    })();

    /* ── the sticky bar ── */
    var bar = $('[data-sbar]');
    var vis = new Map();
    var passed = false;
    function updateBar() {
      if (!bar) return;
      var any = false;
      vis.forEach(function (v) {
        if (v) any = true;
      });
      var on = passed && !any && !drawer.isOpen();
      bar.classList.toggle('is-on', on);
      root.classList.toggle('has-sbar', on);
    }
    if (bar && 'IntersectionObserver' in window) {
      var watched = $$('[data-buy-module]');
      var fin = $('[data-final]');
      if (fin) watched.push(fin);
      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (e) {
            vis.set(e.target, e.isIntersecting);
            if (e.target === main) passed = !e.isIntersecting && e.boundingClientRect.top < 0;
          });
          if (!main) passed = window.scrollY > window.innerHeight;
          updateBar();
        },
        { rootMargin: '-12% 0px -12% 0px' }
      );
      watched.forEach(function (w) {
        io.observe(w);
      });
    }

    emit();
    fetch(base + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.json();
      })
      .then(function (c) {
        var n = c.items
          .filter(function (it) {
            return !!byId[it.variant_id];
          })
          .reduce(function (s, it) {
            return s + it.quantity;
          }, 0);
        var count = $('[data-cart-count]');
        if (count) {
          count.textContent = n;
          count.hidden = !n;
        }
      })
      .catch(function () {});
  }

  function idle(fn) {
    if ('requestIdleCallback' in window) window.requestIdleCallback(fn, { timeout: 1200 });
    else setTimeout(fn, 300);
  }

  function init() {
    reveals();
    nav();
    faq();
    dates();
    gallery();
    commerce();
    how();
    idle(reveal);
    idle(snow);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
